// Type-only import: erased at build time, so this module stays free of runtime
// dependencies while still being checked against the canonical user-type union —
// an API-side rename of 'customer' breaks the comparison below at compile time.
import type { UserType } from 'src/lib/services/api/profile-api/profile-client.types';

// Pure decision for the TrustPilot review request (UserGuiding guide 168973).
//
// When a client rates a revision 4 or 5 stars we ask them for a TrustPilot
// review. Two things are deliberately NOT decided here:
//
//   - How OFTEN a given client may be asked. That lives on the guide in the
//     UserGuiding dashboard and is honoured at runtime because the trigger
//     passes `checkHistory: true`, so CS can retune the cadence without a
//     deploy. See `requestTrustpilotReview`.
//   - Whether the guide renders once triggered — UserGuiding replays the
//     client's own seen/completed history and can still suppress it.
//
// What this file owns is the part that must never drift: the star threshold and
// who is eligible to be asked.

/** Ratings at or above this ask for a review. 1-3 stars never do. */
export const TRUSTPILOT_REVIEW_MIN_RATING = 4;

export interface TrustpilotReviewViewer {
  /** Star rating the viewer just submitted, 1-5. */
  rating: number;
  /** Type of the logged-in user, if it could be determined. */
  userType?: UserType;
  /** True when an admin is acting through the client's account. */
  isImpersonated?: boolean;
  /** True when the SPA is embedded in the mobile app's WebView. */
  isWebView?: boolean;
}

export function shouldRequestTrustpilotReview({
  rating,
  userType,
  isImpersonated,
  isWebView,
}: TrustpilotReviewViewer): boolean {
  if (!Number.isFinite(rating) || rating < TRUSTPILOT_REVIEW_MIN_RATING) return false;

  // Only the client's own users are asked for a review — every user of the
  // company is eligible, since each one can leave their own. Editors and admins
  // who reach this screen must never be prompted, and neither must an admin
  // driving a client account through impersonation: the review would be
  // attributed to a client who never gave it, and it would burn that client's
  // frequency window so the real user is skipped next time.
  if (userType !== 'customer') return false;
  if (isImpersonated) return false;

  // The guide's CTA navigates to trustpilot.com. Inside the mobile app's
  // WebView there is no browser chrome to come back from, so the client would
  // be stranded off the revisions screen — the same reason the HubSpot widget
  // and the email-verification bar are suppressed in a WebView.
  if (isWebView) return false;

  return true;
}
